import React from 'react';
import { useSmartProp, useEditor } from '@smartfire/js-block-engine';
import { MediaPlaceholder, MediaUploadCheck, MediaUpload } from '@wordpress/block-editor';
import { Button, ButtonGroup, IconButton } from '@wordpress/components';
import style from './style.module.scss';

interface ImageSize {
  url: string;
  width: number;
  height: number;
}

interface ImageProps {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  sizes?: Record<string, ImageSize>;
}

interface EditableImgProps {
  smartProp: string;
  className?: string;
  promptChangeText?: string;
  promptPosition?: string;
  loadingStrategy?: 'lazy' | 'eager';
}

export default function EditableImg({
  smartProp,
  className = '',
  promptChangeText = "Changer l'image",
  promptPosition = 'absolute',
  loadingStrategy = 'lazy',
  ...props
}: EditableImgProps) {
  const [img, update] = useSmartProp(smartProp);
  const { isEditing } = useEditor();

  const handleSelectImage = (image: ImageProps) => {
    update(image);
  };

  const renderImage = (image: ImageProps | null) => {
    if (!image || !image.url) return null;

    const srcset = image.sizes
      ? Object.entries(image.sizes)
          .map(([_, size]) => `${size.url} ${size.width}w`)
          .join(', ')
      : undefined;

    const sizes = image.sizes
      ? Object.entries(image.sizes)
          .map(([_, { width }]) => `(max-width: ${width}px) 100vw`)
          .join(', ')
      : undefined;

    return (
      <picture>
        <img
          className={`${style.image} ${className}`}
          src={image.url}
          alt={image.alt || ''}
          srcSet={srcset}
          sizes={sizes || '100vw'}
          loading={loadingStrategy}
          width={image.width}
          height={image.height}
          {...props}
        />
      </picture>
    );
  };

  if (isEditing) {
    return (
      <div
        className={promptPosition === 'relative' ? style.editableImgRelative : style.editableImg}
      >
        {img && img.url ? (
          <>
            <div className={style.buttons}>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={handleSelectImage}
                  allowedTypes={['image']}
                  value={img ? img.id : null}
                  render={({ open }: { open: () => void }) => (
                    <ButtonGroup>
                      <Button isDefault onClick={open}>
                        {promptChangeText}
                      </Button>
                      <IconButton
                        isDefault
                        isDestructive
                        icon="trash"
                        onClick={() => update(null)}
                      />
                    </ButtonGroup>
                  )}
                />
              </MediaUploadCheck>
            </div>
            {renderImage(img)}
          </>
        ) : (
          <MediaUploadCheck>
            <MediaPlaceholder onSelect={handleSelectImage} />
          </MediaUploadCheck>
        )}
      </div>
    );
  }

  return renderImage(img);
}
